import type { CodRiskScoreThresholds } from "../cod-risk-settings";
import {
  RISK_SCORE,
  RISK_THRESHOLDS,
  type CustomerRiskTier,
} from "../risk-constants";
import type { CustomerRiskDTO, CustomerRiskFeaturesDTO } from "../types";

function clampScore(score: number): number {
  return Math.max(0, Math.min(100, Math.round(score)));
}

function parseMoney(value: string | null | undefined): number | null {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function resolveThresholds(
  overrides?: Partial<CodRiskScoreThresholds>,
): typeof RISK_THRESHOLDS & CodRiskScoreThresholds {
  return {
    ...RISK_THRESHOLDS,
    ...overrides,
  };
}

function tierFromScore(
  score: number,
  t: ReturnType<typeof resolveThresholds>,
): {
  tier: CustomerRiskTier;
  suggestedPaymentType: "cod" | "prepaid";
  suggestedDepositPct: number;
} {
  if (score >= t.prepaidScoreMin) {
    return {
      tier: "prepaid_only",
      suggestedPaymentType: "prepaid",
      suggestedDepositPct: 100,
    };
  }
  if (score >= t.depositScoreMin) {
    return {
      tier: "require_deposit",
      suggestedPaymentType: "prepaid",
      suggestedDepositPct: t.defaultDepositPct,
    };
  }
  return {
    tier: "allow_cod",
    suggestedPaymentType: "cod",
    suggestedDepositPct: 0,
  };
}

function pct(rate: number): string {
  return `${(rate * 100).toFixed(0)}%`;
}

/**
 * Pure scoring — unit-testable without DB.
 * Higher score = higher risk. Reasons in Lao (shop UI language).
 */
export function scoreCustomerRisk(
  customerId: string,
  features: CustomerRiskFeaturesDTO,
  options?: {
    proposedTotal?: string | number | null;
    thresholds?: Partial<CodRiskScoreThresholds>;
  },
): CustomerRiskDTO {
  const t = resolveThresholds(options?.thresholds);
  let score = 0;
  const reasons: string[] = [];
  const proposedTotalRaw = options?.proposedTotal;
  const proposedTotal =
    proposedTotalRaw == null || proposedTotalRaw === ""
      ? null
      : String(proposedTotalRaw);
  const proposedNum = parseMoney(proposedTotal);

  if (features.orderCount <= 1) {
    score += RISK_SCORE.newCustomer;
    reasons.push(
      "\u0ea5\u0eb9\u0e81\u0e84\u0ec9\u0eb2\u0ec3\u0edd\u0ec8 — \u0e8d\u0eb1\u0e87\u0e9a\u0ecd\u0ec8\u0ea1\u0eb5\u0e9b\u0eb0\u0eab\u0ea7\u0eb1\u0e94\u0eae\u0eb1\u0e9a\u0eaa\u0eb4\u0e99\u0e84\u0ec9\u0eb2",
    );
  }

  if (features.returnRate != null) {
    if (features.returnRate >= t.highReturnRate) {
      score += RISK_SCORE.highReturnRate;
      reasons.push(
        `\u0ead\u0eb1\u0e94\u0e95\u0eb2\u0e95\u0eb5\u0e81\u0eb1\u0e9a\u0eaa\u0eb9\u0e87 ${pct(features.returnRate)} (${features.returnCount}/${features.shippedLikeCount})`,
      );
    } else if (features.returnRate > 0) {
      score += RISK_SCORE.mildReturnRate;
      reasons.push(
        `\u0ec0\u0e84\u0eb5\u0e8d\u0e95\u0eb5\u0e81\u0eb1\u0e9a ${features.returnCount}/${features.shippedLikeCount} \u0ead\u0ec6\u0ec0\u0e94\u0eb5\u0ec9`,
      );
    }
  }

  if (features.staleCodCount >= 1) {
    score += RISK_SCORE.staleCod;
    reasons.push(
      `\u0ec0\u0e84\u0eb5\u0e8d\u0e84\u0ec9\u0eb2\u0e87\u0eae\u0eb1\u0e9a COD \u0ec0\u0e81\u0eb5\u0e99 ${t.staleShippedCodDays} \u0ea7\u0eb1\u0e99 (${features.staleCodCount} \u0ead\u0ec6\u0ec0\u0e94\u0eb5\u0ec9)`,
    );
  }

  if (
    features.codOrderCount >= 1 &&
    features.codSuccessRate != null &&
    features.codSuccessRate < t.lowCodSuccessRate
  ) {
    score += RISK_SCORE.lowCodSuccess;
    reasons.push(
      `COD \u0eaa\u0eb3\u0ec0\u0ea5\u0eb1\u0e94\u0e95\u0ec8\u0eb3 ${pct(features.codSuccessRate)} (${features.codPickedUpCount}/${features.codOrderCount})`,
    );
  }

  if (features.isReturning && features.returnCount === 0) {
    score += RISK_SCORE.returningNoReturn;
    reasons.push(
      "\u0ea5\u0eb9\u0e81\u0e84\u0ec9\u0eb2\u0ec0\u0e81\u0ebb\u0ec8\u0eb2 — \u0e9a\u0ecd\u0ec8\u0ec0\u0e84\u0eb5\u0e8d\u0e95\u0eb5\u0e81\u0eb1\u0e9a",
    );
  }

  if (
    features.codOrderCount >= t.minCodOrdersForStrongBonus &&
    features.codSuccessRate != null &&
    features.codSuccessRate >= t.strongCodSuccessRate
  ) {
    score += RISK_SCORE.strongCodSuccess;
    reasons.push(
      `COD \u0eaa\u0eb3\u0ec0\u0ea5\u0eb1\u0e94\u0e94\u0eb5 ${pct(features.codSuccessRate)} (\u2265${t.minCodOrdersForStrongBonus} \u0ead\u0ec6\u0ec0\u0e94\u0eb5\u0ec9)`,
    );
  }

  const avg = parseMoney(features.avgOrderValue);
  if (proposedNum != null && avg != null && avg > 0 && proposedNum > 2 * avg) {
    score += RISK_SCORE.proposedAbove2xAvg;
    reasons.push(
      "\u0e8d\u0ead\u0e94\u0ead\u0ec6\u0ec0\u0e94\u0eb5\u0ec9\u0e9b\u0eb1\u0e94\u0e88\u0eb8\u0e9a\u0eb1\u0e99\u0eaa\u0eb9\u0e87\u0e81\u0ea7\u0ec8\u0eb2 2\u00d7 \u0e84\u0ec8\u0eb2\u0eaa\u0eb0\u0ec0\u0ea5\u0ec8\u0e8d\u0e82\u0ead\u0e87\u0ea5\u0eb9\u0e81\u0e84\u0ec9\u0eb2",
    );
  }

  if (
    features.orderCount <= 1 &&
    proposedNum != null &&
    proposedNum >= t.newCustomerTotalLak
  ) {
    score += RISK_SCORE.newCustomerHighTotal;
    reasons.push(
      `\u0ea5\u0eb9\u0e81\u0e84\u0ec9\u0eb2\u0ec3\u0edd\u0ec8 + \u0e8d\u0ead\u0e94\u0eaa\u0eb9\u0e87 (\u2265 ${t.newCustomerTotalLak.toLocaleString("en-US")} \u0e81\u0eb5\u0e9a)`,
    );
  }

  if (
    features.branchReturnRate != null &&
    features.branchShippedCount >= t.minBranchSample
  ) {
    const branchLabel = features.lastShippingBranch
      ? `\u0eaa\u0eb2\u0e82\u0eb2 ${features.lastShippingBranch}`
      : (features.lastShippingCarrier ?? "carrier");
    const carrierHint = features.lastShippingCarrier
      ? ` (${features.lastShippingCarrier})`
      : "";
    if (features.branchReturnRate >= t.highBranchReturnRate) {
      score += RISK_SCORE.highBranchReturnRate;
      reasons.push(
        `${branchLabel}${carrierHint} \u0e95\u0eb5\u0e81\u0eb1\u0e9a ${pct(features.branchReturnRate)} — \u0e9e\u0eb4\u0e88\u0eb2\u0ea5\u0eb0\u0e99\u0eb2 COD`,
      );
    } else if (features.branchReturnRate >= t.mildBranchReturnRate) {
      score += RISK_SCORE.mildBranchReturnRate;
      reasons.push(
        `${branchLabel}${carrierHint} \u0e95\u0eb5\u0e81\u0eb1\u0e9a ${pct(features.branchReturnRate)} — \u0e84\u0ea7\u0e99\u0e88\u0eb1\u0e9a\u0e95\u0eb2 COD`,
      );
    }
  }

  const clamped = clampScore(score);
  const { tier, suggestedPaymentType, suggestedDepositPct } = tierFromScore(
    clamped,
    t,
  );

  if (reasons.length === 0) {
    reasons.push(
      "\u0e9a\u0ecd\u0ec8\u0ea1\u0eb5\u0eaa\u0eb1\u0e8d\u0e8d\u0eb2\u0e99\u0e84\u0ea7\u0eb2\u0ea1\u0eaa\u0ec8\u0e8d\u0e87\u0e9e\u0eb4\u0ec0\u0eaa\u0e94 — \u0e84\u0ea7\u0eb2\u0ea1\u0eaa\u0ec8\u0e8d\u0e87\u0e95\u0ec8\u0eb3",
    );
  }

  return {
    customerId,
    score: clamped,
    tier,
    suggestedPaymentType,
    suggestedDepositPct,
    reasons,
    features,
    proposedTotal,
  };
}
